import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, sinCache, AVISO_PROTEGIDO } from "@/lib/api";
import { pedirContrasenaPdf } from "@/components/ContrasenaPdf";
import { toast } from "sonner";
import { Spinner } from "@/components/common";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  X, Download, ZoomIn, ZoomOut, Maximize, Minimize, RotateCw,
  RefreshCw, FileWarning, MoveHorizontal, ShieldCheck, ShieldAlert, Printer, Lock,
} from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const toolBtn =
  "flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";

function IntegridadBar({ info }) {
  if (!info?.estado) return null;
  const ok = info.estado === "verificada";
  const txt = ok ? "Integridad verificada" : info.estado === "alterada" ? "ALERTA: el archivo no coincide con su huella oficial" : "Documento sin huella registrada";
  return (
    <div className={`flex flex-wrap items-center gap-2 px-4 py-1.5 text-[11px] ${ok ? "bg-emerald-900/60 text-emerald-200" : "bg-amber-900/60 text-amber-200"}`} data-testid="visor-integridad">
      {ok ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
      <span className="font-semibold">{txt}</span>
      {info.version && <span>· versión {info.version}</span>}
      <span className="break-all font-mono opacity-80" data-testid="visor-hash">· SHA-256 {info.hash}</span>
    </div>
  );
}

export default function DocViewer({ url, titulo, subtitulo, nombreArchivo, onClose, onLoaded, onFinLeido, children }) {
  const [integ, setInteg] = useState(null);
  const [status, setStatus] = useState("cargando"); // cargando | error | listo
  const [error, setError] = useState("");
  const [blob, setBlob] = useState(null);
  const [contentType, setContentType] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [fitWidth, setFitWidth] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [isFull, setIsFull] = useState(false);
  const [retry, setRetry] = useState(0);
  const wrapRef = useRef(null);
  const bodyRef = useRef(null);
  const [bodyWidth, setBodyWidth] = useState(800);
  const [protegido, setProtegido] = useState(false);
  const [pwd, setPwd] = useState(null);
  const [pide, setPide] = useState(null);

  useEffect(() => { setPwd(null); }, [url]);

  useEffect(() => {
    let cancelled = false;
    setStatus("cargando");
    setError("");
    setBlob(null); // nunca mostrar el documento anterior al cambiar
    setNumPages(0);
    setScale(1);
    setFitWidth(true);
    setRotation(0);
    api
      .get(sinCache(url), { responseType: "blob", headers: { "Cache-Control": "no-cache", ...(pwd ? { "X-Pdf-Password": pwd } : {}) } })
      .then((res) => {
        if (cancelled) return;
        setProtegido(!!res.headers["x-documento-protegido"]);
        setPide(null);
        setContentType(res.headers["content-type"] || res.data.type || "");
        const info = { estado: res.headers["x-integridad"], hash: res.headers["x-documento-sha256"], version: res.headers["x-documento-version"] };
        setInteg(info);
        onLoaded?.(info);
        setBlob(res.data);
        setStatus("listo");
      })
      .catch(async (err) => {
        if (cancelled) return;
        let msg = "No fue posible visualizar el archivo.";
        const data = err.response?.data;
        let det = null;
        if (data instanceof Blob) {
          try { det = JSON.parse(await data.text()).detail; } catch { /* sin detalle */ }
        } else {
          det = data?.detail;
        }
        msg = formatApiErrorDetail(det) || msg;
        if (det?.code === "pdf_protegido_sin_contrasena" || det?.code === "pdf_contrasena_incorrecta") {
          setPide({ ...det, error: det.code === "pdf_contrasena_incorrecta", puede_guardar: det.puede_guardar ?? pide?.puede_guardar });
          setStatus("protegido");
          return;
        }
        setError(err.response?.status === 404 ? "El archivo no está disponible." : msg);
        setStatus("error");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, retry, pwd]);

  const ingresarClave = async () => {
    const v = await pedirContrasenaPdf({
      mensaje: "Este PDF está protegido con contraseña. Ingrésala para visualizarlo.", error: pide?.error,
      nota: pide?.puede_guardar ? "Roge guardará la contraseña cifrada para que las personas autorizadas puedan verlo. El archivo original no se modifica." : "La contraseña solo se usa para esta visualización. El archivo original no se modifica.",
      textoCancelar: "Cancelar", textoConfirmar: "Abrir documento",
    });
    if (v) { setPwd(v); setRetry((r) => r + 1); }
  };

  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    const measure = () => { if (bodyRef.current) setBodyWidth(bodyRef.current.clientWidth - 24); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [status]);

  const guardarBlob = (b) => {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(b);
    link.setAttribute("download", nombreArchivo || "documento");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const descargar = async () => {
    if (!protegido && blob) { guardarBlob(blob); return; }
    try {
      const res = await api.get(sinCache(`${url}${url.includes("?") ? "&" : "?"}original=true`), { responseType: "blob" });
      guardarBlob(res.data);
      toast.warning(AVISO_PROTEGIDO);
    } catch (err) {
      let m = "No fue posible descargar el archivo original.";
      try { m = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail); } catch { /* sin detalle */ }
      toast.error(m);
    }
  };

  const imprimir = () => {
    if (!blob) return;
    const src = window.URL.createObjectURL(blob);
    const f = document.createElement("iframe");
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    f.onload = () => {
      const lanzar = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(src, "_blank"); } };
      const img = f.contentDocument?.querySelector?.("img");
      if (img && !img.complete) img.onload = lanzar; else lanzar();
    };
    if (contentType.startsWith("image/")) {
      f.srcdoc = `<html><head><style>@page{margin:12mm}body{margin:0;display:flex;justify-content:center}img{max-width:100%;max-height:100vh;object-fit:contain}</style></head><body><img src="${src}"/></body></html>`;
    } else {
      f.src = src;
    }
    document.body.appendChild(f);
    setTimeout(() => { f.remove(); window.URL.revokeObjectURL(src); }, 120000);
  };

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapRef.current?.requestFullscreen?.();
  };

  const esPdf = contentType.includes("pdf");
  const esImagen = contentType.startsWith("image/");
  const imgUrl = useMemo(
    () => (blob && esImagen ? window.URL.createObjectURL(blob) : null),
    [blob, esImagen]
  );
  useEffect(() => () => { if (imgUrl) window.URL.revokeObjectURL(imgUrl); }, [imgUrl]);
  const zoomIn = () => { setFitWidth(false); setScale((s) => Math.min(3, +(s + 0.2).toFixed(2))); };
  const zoomOut = () => { setFitWidth(false); setScale((s) => Math.max(0.4, +(s - 0.2).toFixed(2))); };
  const ajustar = () => { setFitWidth(true); setScale(1); };
  // Avisa cuando el usuario llegó al final del documento (o si cabe completo en pantalla).
  const revisarFin = () => {
    const el = bodyRef.current;
    if (el && onFinLeido && numPages > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 60) onFinLeido();
  };

  return (
    <div ref={wrapRef} className="fixed inset-0 z-[60] flex flex-col bg-slate-900/95" data-testid="visor-documento">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 bg-slate-900 px-4 py-2.5">
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          <p className="truncate text-sm font-semibold text-white" data-testid="visor-titulo">{titulo}</p>
          {subtitulo && <p className="truncate text-xs text-slate-400">{subtitulo}</p>}
        </div>
        {status === "listo" && (
          <>
            <button onClick={zoomOut} disabled={esImagen ? scale <= 0.4 : fitWidth} className={toolBtn} data-testid="btn-zoom-out" title="Alejar"><ZoomOut className="h-3.5 w-3.5" /></button>
            <span className="w-14 text-center text-xs text-slate-300" data-testid="nivel-zoom">{fitWidth && esPdf ? "Ancho" : `${Math.round(scale * 100)}%`}</span>
            <button onClick={zoomIn} className={toolBtn} data-testid="btn-zoom-in" title="Acercar"><ZoomIn className="h-3.5 w-3.5" /></button>
            {esPdf && (
              <button onClick={ajustar} disabled={fitWidth} className={toolBtn} data-testid="btn-ajustar-ancho" title="Ajustar al ancho"><MoveHorizontal className="h-3.5 w-3.5" /> Ajustar</button>
            )}
            {esImagen && (
              <button onClick={() => setRotation((r) => (r + 90) % 360)} className={toolBtn} data-testid="btn-rotar" title="Rotar"><RotateCw className="h-3.5 w-3.5" /></button>
            )}
            {(esPdf || esImagen) && <button onClick={imprimir} disabled={!blob} className={toolBtn} data-testid="btn-imprimir-visor"><Printer className="h-3.5 w-3.5" /> Imprimir</button>}
            <button onClick={descargar} className={toolBtn} data-testid="btn-descargar-visor"><Download className="h-3.5 w-3.5" /> Descargar</button>
          </>
        )}
        {status === "protegido" && (
          <button onClick={descargar} className={toolBtn} data-testid="btn-descargar-original-protegido"><Download className="h-3.5 w-3.5" /> Descargar original</button>
        )}
        <button onClick={toggleFull} className={toolBtn} data-testid="btn-pantalla-completa" title="Pantalla completa">
          {isFull ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
        </button>
        <button onClick={onClose} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800" data-testid="btn-cerrar-visor">
          <X className="h-3.5 w-3.5" /> Cerrar
        </button>
      </div>

      <IntegridadBar info={integ} />
      {protegido && status === "listo" && (
        <div className="flex items-center gap-2 bg-amber-900/60 px-4 py-1.5 text-[11px] text-amber-100" data-testid="visor-aviso-protegido">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>Documento protegido con contraseña. Ves una copia temporal de lectura; el archivo original se conserva intacto y al descargarlo pedirá la contraseña.</span>
        </div>
      )}
      {children && <div className="border-b border-white/10 bg-slate-900 px-4 py-2">{children}</div>}

      {/* Cuerpo */}
      <div ref={bodyRef} onScroll={revisarFin} className="flex-1 overflow-auto p-3">
        {status === "cargando" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-300" data-testid="visor-cargando">
            <Spinner className="h-7 w-7" />
            <p className="text-sm">Cargando documento…</p>
          </div>
        )}
        {status === "protegido" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center" data-testid="visor-protegido">
            <Lock className="h-10 w-10 text-amber-400" />
            <p className="max-w-md text-sm font-semibold text-white">Documento protegido con contraseña</p>
            <p className="max-w-md text-sm text-slate-300">{pide?.error ? "La contraseña ingresada no abre el documento." : "Roge no tiene registrada la contraseña de este PDF. Ingrésala para visualizarlo, o descarga el archivo original (pedirá la contraseña al abrirlo)."}</p>
            <div className="flex flex-wrap justify-center gap-2">
              <button onClick={ingresarClave} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800" data-testid="btn-ingresar-contrasena">
                <Lock className="h-4 w-4" /> Ingresar contraseña
              </button>
              <button onClick={descargar} className="flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10" data-testid="btn-descargar-protegido">
                <Download className="h-4 w-4" /> Descargar original
              </button>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="visor-error">
            <FileWarning className="h-10 w-10 text-amber-400" />
            <p className="max-w-md text-sm text-slate-200">{error}</p>
            <button onClick={() => setRetry((r) => r + 1)} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800" data-testid="btn-reintentar-visor">
              <RefreshCw className="h-4 w-4" /> Reintentar
            </button>
          </div>
        )}
        {status === "listo" && esPdf && (
          <div className="mx-auto flex flex-col items-center gap-3" data-testid="visor-pdf">
            <Document
              file={blob}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={() => { setError("No se pudo leer el PDF."); setStatus("error"); }}
              loading={<Spinner className="h-7 w-7 text-slate-300" />}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={`${url}-p${i + 1}`}
                  pageNumber={i + 1}
                  width={fitWidth ? Math.min(bodyWidth, 1000) : undefined}
                  scale={fitWidth ? 1 : scale}
                  className="shadow-lg"
                  onRenderSuccess={i + 1 === numPages ? revisarFin : undefined}
                />
              ))}
            </Document>
            {numPages > 0 && <p className="text-xs text-slate-400">{numPages} página{numPages > 1 ? "s" : ""}</p>}
          </div>
        )}
        {status === "listo" && esImagen && (
          <div className="flex min-h-full items-center justify-center" data-testid="visor-imagen">
            <img
              src={imgUrl}
              alt={titulo}
              style={{ transform: `scale(${scale}) rotate(${rotation}deg)`, transition: "transform 0.2s" }}
              className="max-h-[85vh] max-w-full object-contain shadow-lg"
            />
          </div>
        )}
        {status === "listo" && !esPdf && !esImagen && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="visor-no-soportado">
            <FileWarning className="h-10 w-10 text-slate-400" />
            <p className="max-w-md text-sm text-slate-200">
              Este formato ({contentType || "desconocido"}) no se puede visualizar dentro de la aplicación.
              El archivo original se conserva intacto; puedes descargarlo de forma autorizada.
            </p>
            <button onClick={descargar} className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800" data-testid="btn-descargar-fallback">
              <Download className="h-4 w-4" /> Descargar archivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
